import { createAsyncThunk } from "@reduxjs/toolkit";
import { omitBy } from "lodash";
import { normalize } from "normalizr";
import coreAPIs from "services/api/core";
import publishingCloudAPIs from "services/api/publisher/publishingCloudAPIs";
import { distinctListPublishes } from "store/publish/actions";
import { addBulkPublishItems } from "store/publish/slice";

import magnifiAPI from "../../services/api/magnifi";
import { clip, clipListGroupByDate } from "../schemas";

export const getClips = createAsyncThunk("clip/getClips", async (payload) => {
  try {
    const response = await magnifiAPI.getClips(payload);

    return {
      ...response.data,
      ...(response.data.success ? normalize(response.data.clips, [clip]) : {}),
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getClipsByIds = createAsyncThunk("clip/getClipsByIds", async (payload, { dispatch }) => {
  try {
    const response = await magnifiAPI.getClipsByIds(payload);

    const clipIds = response?.data?.data?.map((clip) => clip._id);
    const entityId = payload?.payload?.entityId;
    if (clipIds?.length && entityId) {
      // Process clip IDs in batches of 10 to avoid overloading the API
      const batchSize = 10;
      const clipIdBatches = [];

      // Split clipIds into batches of batchSize
      for (let i = 0; i < clipIds.length; i += batchSize) {
        clipIdBatches.push(clipIds.slice(i, i + batchSize));
      }

      // Process each batch
      for (const batchIds of clipIdBatches) {
        const publishesPayload = {
          append: true,
          payload: {
            page: 1,
            pageSize: 100,
            entityId,
            filters: {
              contentIds: batchIds,
              statuses: ["completed"],
            },
          },
        };

        dispatch(distinctListPublishes(publishesPayload));
      }
    }

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const generateNewClip = createAsyncThunk("clip/generateNewClip", async (payload) => {
  try {
    const response = await magnifiAPI.generateNewClip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const deleteBulkClips = createAsyncThunk("clip/deleteBulkClips", async (payload) => {
  try {
    const response = await magnifiAPI.deleteBulkClips(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const copyClipsToStream = createAsyncThunk("clip/copyClipsToStream", async (payload) => {
  try {
    const response = await magnifiAPI.copyClipsToStream(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getAutoTitle = createAsyncThunk("clip/getAutoTitle", async (payload) => {
  try {
    const response = await magnifiAPI.getAutoTitle(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const createClip = createAsyncThunk("clip/createClip", async (payload) => {
  try {
    const response = await magnifiAPI.createClip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const createClipV2 = createAsyncThunk("clip/createClipV2", async (payload) => {
  try {
    const response = await magnifiAPI.createClipV2(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const bulkPublishClipsToCloud = createAsyncThunk("clip/publishClipsToCloud", async (payload) => {
  try {
    const response = await publishingCloudAPIs.bulkCloudPublish(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error?.response?.data?.error?.message?.toString() || error;
  }
});

export const downloadAllClips = createAsyncThunk("clip/downloadAllClips", async (payload) => {
  try {
    const response = await magnifiAPI.downloadAllClips(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const downloadAsZip = createAsyncThunk("clip/downloadAsZip", async (payload) => {
  try {
    const response = await magnifiAPI.downloadAsZip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const clipsDownloadVttsAsZip = createAsyncThunk("clip/clipsDownloadVttsAsZip", async (payload) => {
  try {
    const response = await magnifiAPI.clipsDownloadVttsAsZip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const exportClipJSON = createAsyncThunk("clip/exportClipJSON", async (payload) => {
  try {
    const response = await magnifiAPI.exportClipJSON(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const exportClipEDL = createAsyncThunk("clip/exportClipEDL", async (payload) => {
  try {
    const response = await magnifiAPI.exportClipEDL(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const exportClipXML = createAsyncThunk("clip/exportClipXML", async (payload) => {
  try {
    const response = await magnifiAPI.exportClipXML(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const editClip = createAsyncThunk("clip/editClip", async (payload) => {
  try {
    const response = await magnifiAPI.editClip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const saveInningsData = createAsyncThunk("clip/saveInningsData", async (payload) => {
  try {
    const response = await magnifiAPI.saveInningsData(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const checkAutoAssignRating = createAsyncThunk("clip/checkAutoAssignRating", async (payload) => {
  try {
    const response = await magnifiAPI.checkAutoAssignRating(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const generateThumbnail = createAsyncThunk("clip/generateThumbnail", async (payload) => {
  try {
    const response = await magnifiAPI.generateThumbnail(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getClipDetails = createAsyncThunk("clip/getClipDetails", async (payload) => {
  try {
    const response = await magnifiAPI.getClipDetails(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const addSocialMediaData = createAsyncThunk("clip/addSocialMediaData", async (payload) => {
  try {
    const response = await magnifiAPI.addSocialMediaData(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const addPublishNotification = createAsyncThunk("clip/addPublishNotification", async (payload) => {
  try {
    const response = await magnifiAPI.addPublishNotification(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const generateSocialMediaCaption = createAsyncThunk("clip/generateSocialMediaCaption", async (payload) => {
  try {
    const response = await magnifiAPI.generateSocialMediaCaption(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const generateAutoFlipClip = createAsyncThunk("clip/generateAutoFlipClip", async (payload) => {
  try {
    const response = await magnifiAPI.generateAutoFlipClip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const generateInputRatioClip = createAsyncThunk("clip/generateInputRatioClip", async (payload) => {
  try {
    const response = await magnifiAPI.generateInputRatioClip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const deleteEditedClip = createAsyncThunk("clip/deleteEditedClip", async (payload) => {
  try {
    const response = await magnifiAPI.deleteEditedClip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const overwriteEditedClip = createAsyncThunk("clip/overwriteEditedClip", async (payload) => {
  try {
    const response = await magnifiAPI.overwriteEditedClip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const updateClipDetails = createAsyncThunk("clip/updateClipDetails", async (payload) => {
  try {
    const response = await magnifiAPI.updateClipDetails(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const copyClipToReviewers = createAsyncThunk("clip/copyClipToReviewers", async (payload) => {
  try {
    const response = await magnifiAPI.copyClipToReviewers(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getClipPublicUrl = createAsyncThunk("clip/getClipPublicUrl", async (payload) => {
  try {
    const response = await magnifiAPI.getClipPublicUrl(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const uploadClipToBrightCove = createAsyncThunk("clip/uploadClipToBrightCove", async (payload) => {
  try {
    const response = await magnifiAPI.uploadClipToBrightCove(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const editClipDataForFanCode = createAsyncThunk("clip/editClipDataForFanCode", async (payload) => {
  try {
    const response = await magnifiAPI.editClipDataForFanCode(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getClipMetadata = createAsyncThunk("clip/getClipMetadata", async (payload) => {
  try {
    const response = await magnifiAPI.getClipMetadata(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const updateSftp = createAsyncThunk("clip/updateSftp", async (payload) => {
  try {
    const response = await magnifiAPI.updateSftp(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const clientWebhookPublish = createAsyncThunk("clip/clientWebhookPublish", async (payload) => {
  try {
    const response = await magnifiAPI.clientWebhookPublish(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const publishClip = createAsyncThunk("clip/publishClip", async (payload) => {
  try {
    const response = await magnifiAPI.publishClip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const trimClip = createAsyncThunk("clip/trimClip", async (payload) => {
  try {
    const response = await magnifiAPI.trimClip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getRecentClipsLists = createAsyncThunk("clip/getRecentClips", async (payload) => {
  try {
    const response = await magnifiAPI.getRecentClips(payload);
    const clipsData = response.data?.clips || {};

    // sorting clips based on updatedAt, to show latest clips on top for a particular date
    clipsData.forEach(
      (clip) => (clip.clips = clip.clips?.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))),
    );
    const normalizedData = normalize(clipsData, [clipListGroupByDate]);

    return {
      ...response.data,
      ...(response.data.success ? normalizedData : {}),
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const createIllustoProject = createAsyncThunk("clip/createIllustoProject", async (payload) => {
  try {
    const response = await magnifiAPI.createIllustoProject(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const downloadClipZipPackage = createAsyncThunk("clip/downloadClipZipPackage", async (payload) => {
  try {
    const response = await magnifiAPI.downloadClipZipPackage(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getPreAssignedUrl = createAsyncThunk("clip/getPreAssignedUrl", async (payload) => {
  try {
    const response = await magnifiAPI.getPreAssignedUrl(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getUploadedClipDetail = createAsyncThunk("clip/uploadedClipDetail", async (payload) => {
  try {
    const response = await coreAPIs.getUploadedClipDetail(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});
export const uploadClip = createAsyncThunk("clip/uploadClip", async (payload) => {
  try {
    const response = await coreAPIs.uploadClip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});
export const applyLogoToThumbnail = createAsyncThunk("clip/applyLogoToThumbnail", async (payload) => {
  try {
    const response = await magnifiAPI.applyLogoToThumbnail(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getClipDetailsByProjectId = createAsyncThunk("clip/getClipDetailsByProjectId", async (payload) => {
  try {
    const response = await magnifiAPI.getClipDetailsByProjectId(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getUploadPicture = createAsyncThunk("clip/getUploadPicture", async (payload) => {
  try {
    const response = await magnifiAPI.getUploadPicture(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const generateCLipPreview = createAsyncThunk("clip/generateCLipPreview", async (payload) => {
  try {
    const response = await magnifiAPI.generateCLipPreview(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const uploadToThirdPartyS3Publish = createAsyncThunk("clip/uploadToThirdPartyS3Publish", async (payload) => {
  try {
    const response = await magnifiAPI.uploadToThirdPartyS3Publish(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const s3BucketPublish = createAsyncThunk("clip/s3BucketPublish", async (payload) => {
  try {
    const response = await magnifiAPI.s3BucketPublish(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const s3BucketPublishHighlights = createAsyncThunk(
  "clip/s3BucketPublishHighlights",
  async ({ resourceType, payload }) => {
    try {
      console.log({ resourceType, payload });
      const response = await magnifiAPI.s3BucketPublishHighlights({ resourceType, payload });

      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
);

export const verifySpecification = createAsyncThunk("clip/verifySpecification", async (payload) => {
  try {
    const response = await magnifiAPI.verifySpecification(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getArchivalFiltersList = createAsyncThunk("clip/get-archival-filters-list", async ({ orgId, payload }) => {
  try {
    const response = await magnifiAPI.getArchivalFiltersList({ orgId, payload });

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getArchivalClips = createAsyncThunk("clip/get-archival-clips", async ({ orgId, payload }) => {
  try {
    const response = await magnifiAPI.getArchivalClips({ orgId, payload });

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getPublishedClips = createAsyncThunk("clip/getPublishedClips", async (payload) => {
  try {
    const response = await magnifiAPI.getPublishedClips(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getClipsByTimeRange = createAsyncThunk("clip/getClipsByTimeRange", async (payload) => {
  try {
    const response = await magnifiAPI.getClipsByTimeRange(payload);

    return { ...response.data, ...(response.data.success ? normalize(response.data.clips, [clip]) : {}) };
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getFoldersByClipId = createAsyncThunk("clip/getFoldersByClipId", async ({ clipId, payload }) => {
  try {
    const response = await magnifiAPI.getFoldersByClipId({ clipId, payload });

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const exportPartnerClipJSON = createAsyncThunk("clip/exportPartnerClipJSON", async (payload) => {
  try {
    const response = await magnifiAPI.exportPartnerClipJSON(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const exportPartnerHLClipJSON = createAsyncThunk("clip/exportPartnerHLClipJSON", async (payload) => {
  try {
    const response = await magnifiAPI.exportPartnerHLClipJSON(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const exportPartnerHighlightJSON = createAsyncThunk("clip/exportPartnerHighlightJSON", async (payload) => {
  try {
    const response = await magnifiAPI.exportPartnerHighlightJSON(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const mediaConvertClipRequest = createAsyncThunk("clip/mediaConvertClipRequest", async (payload) => {
  try {
    const response = await magnifiAPI.mediaConvertClipRequest(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const fetchMediaConvertClipVideos = createAsyncThunk("clip/fetchMediaConvertClipVideos", async (payload) => {
  try {
    const response = await magnifiAPI.fetchMediaConvertClipVideos(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const mediaConvertClipByTemplateRequest = createAsyncThunk(
  "clip/mediaConvertClipByTemplateRequest",
  async (payload) => {
    try {
      const response = await magnifiAPI.mediaConvertClipByTemplateRequest(payload);

      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
);

export const updateClipsDetails = createAsyncThunk("clip/updateClipsDetails", async (payload) => {
  try {
    const response = await magnifiAPI.updateClipsDetails(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getClipNamingTemplates = createAsyncThunk("clip/getClipNamingTemplates", async (payload) => {
  try {
    const response = await magnifiAPI.getClipNamingTemplates(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const jsonBucketPublishToCloud = createAsyncThunk("clip/jsonBucketPublishToCloud", async (payload) => {
  try {
    const response = await magnifiAPI.jsonBucketPublishToCloud(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const createS3PresignedUrl = createAsyncThunk("clip/createS3PresignedUrl", async (payload) => {
  try {
    const response = await magnifiAPI.createS3PresignedUrl(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getNextClipIdForClipCDTeamCustomEditModal = createAsyncThunk(
  "clip/getNextClipIdForClipCDTeamCustomEditModal",
  async (payload, { getState }) => {
    const state = getState();
    const clipState = state.clip;
    const incrementVal = payload?.incrementLength || 1;
    const modifiedClipIndex = clipState.modals.clipCDTeamCustomEditModal.selectedClipIndex + incrementVal;

    if (modifiedClipIndex > clipState.ids.length - 1) {
      return {
        newClipId: null,
        referenceClipId: null,
        clipIndex: modifiedClipIndex,
      };
    }

    const newClipId = clipState.ids[modifiedClipIndex];
    const clipDetails = clipState.entities[newClipId];

    return {
      newClipId,
      referenceClipId: clipDetails?.referenceClipId ? clipDetails?.referenceClipId : null,
      clipIndex: modifiedClipIndex,
    };
  },
);

export const getPrevClipIdForClipCDTeamCustomEditModal = createAsyncThunk(
  "clip/getPrevClipIdForClipCDTeamCustomEditModal",
  async (payload, { getState }) => {
    const state = getState();
    const clipState = state.clip;
    const decrementVal = payload?.decrementLength || 1;
    const modifiedClipIndex = clipState.modals.clipCDTeamCustomEditModal.selectedClipIndex - decrementVal;

    if (modifiedClipIndex < 0) {
      return {
        newClipId: null,
        referenceClipId: null,
        clipIndex: modifiedClipIndex,
      };
    }

    const newClipId = clipState.ids[modifiedClipIndex];
    const clipDetails = clipState.entities[newClipId];

    return {
      newClipId,
      referenceClipId: clipDetails?.referenceClipId ? clipDetails?.referenceClipId : null,
      clipIndex: modifiedClipIndex,
    };
  },
);

export const getLiveEditFilters = createAsyncThunk("clip/getLiveEditFilters", async (payload) => {
  try {
    const response = await magnifiAPI.getLiveEditFilters(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getPublishedClipsByStreamId = createAsyncThunk(
  "clip/getPublishedClipsByStreamId",
  async (payload, { dispatch }) => {
    try {
      const response = await magnifiAPI.getPublishedClipsByStreamId(payload);

      if (response?.data) {
        dispatch(addBulkPublishItems(response?.data));
      }

      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
);

export const getClipsAndPublishes = createAsyncThunk("clip/getClipsAndPublishes", async (payload, { dispatch }) => {
  try {
    let clipIds = [];

    if (payload.clipsList) {
      clipIds = payload.clipsList.map((clip) => clip._id);
    } else {
      const clipsResponse = await dispatch(getClips(omitBy(payload, (_, key) => key === "clipsList"))).unwrap();

      clipIds = clipsResponse?.clips?.map((clip) => clip._id);
    }

    if (clipIds?.length && payload?.entityId) {
      // Process clip IDs in batches of 10 to avoid overloading the API
      const batchSize = 10;
      const clipIdBatches = [];

      // Split clipIds into batches of batchSize
      for (let i = 0; i < clipIds.length; i += batchSize) {
        clipIdBatches.push(clipIds.slice(i, i + batchSize));
      }

      // Process each batch
      for (const batchIds of clipIdBatches) {
        const publishesPayload = {
          append: true,
          payload: {
            page: 1,
            pageSize: 100,
            entityId: payload?.entityId,
            filters: {
              contentIds: batchIds,
              statuses: ["completed"],
            },
          },
        };

        dispatch(distinctListPublishes(publishesPayload));
      }
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const createClipNamingTemplate = createAsyncThunk("clip/createClipNamingTemplate", async (payload) => {
  try {
    const response = await magnifiAPI.createClipNamingTemplate(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const updateClipNamingTemplate = createAsyncThunk("clip/updateClipNamingTemplate", async (payload) => {
  try {
    const response = await magnifiAPI.updateClipNamingTemplate(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const deleteClipNamingTemplate = createAsyncThunk("clip/deleteClipNamingTemplate", async (payload) => {
  try {
    const response = await magnifiAPI.deleteClipNamingTemplate(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const markClipNamingTemplateAsActive = createAsyncThunk(
  "clip/markClipNamingTemplateAsActive",
  async (payload) => {
    try {
      const response = await magnifiAPI.markClipNamingTemplateAsActive(payload);

      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
);

export const editClipVideos = createAsyncThunk("clip/editClipVideos", async (payload) => {
  try {
    const response = await magnifiAPI.editClipVideos(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const generateClipVideosThumbnail = createAsyncThunk("clip/generateClipVideosThumbnail", async (payload) => {
  try {
    const response = await magnifiAPI.generateClipVideosThumbnail(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const cancelAspectRatioGenerationRequest = createAsyncThunk(
  "clip/cancelAspectRatioGenerationRequest",
  async (payload) => {
    try {
      const response = await magnifiAPI.cancelAspectRatioGenerationRequest(payload);

      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
);

export const getClipsTemplateDetails = createAsyncThunk("clip/getClipsTemplateDetails", async (payload) => {
  try {
    const response = await magnifiAPI.getClipsTemplateDetails(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});
